/**
 * Safely redirect the user to a third-party URL (Stripe checkout, OAuth provider, etc).
 *
 * WHY THIS EXISTS
 * ---------------
 * When the PJP app is rendered inside the Emergent preview iframe (or any other
 * iframe wrapper), a plain `window.location.href = url` navigates only the inner
 * iframe. Stripe Checkout responds with `X-Frame-Options: DENY`, so the browser
 * refuses to render it — the user sees a blank / white page.
 *
 * This helper always breaks out to the top-level browsing context. If we are
 * already at the top, it is equivalent to `window.location.href = url`.
 *
 * If top-level navigation is blocked by browser sandbox rules, we fall back to
 * opening the destination in a brand-new tab so the user can still complete the
 * flow.
 *
 * @param {string} url  Absolute URL to navigate to. Must start with `http`.
 * @returns {boolean}   `true` if navigation was scheduled, `false` if we couldn't
 *                      navigate at all (caller should surface an error).
 */
export function redirectExternal(url) {
  if (!url || typeof url !== "string" || !/^https?:\/\//i.test(url)) {
    return false;
  }
  try {
    // Preferred: replace the TOP window so Stripe/OAuth pages get their own
    // top-level browsing context and the return URL comes back to our origin
    // seamlessly.
    if (window.top && window.top !== window) {
      window.top.location.href = url;
      return true;
    }
    // Not in an iframe — normal same-tab navigation.
    window.location.href = url;
    return true;
  } catch (_e) {
    // Cross-origin frame-lock (some sandboxed previews) — fall back to a new tab.
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    return !!opened;
  }
}
